import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, eur } from "@/lib/api";
import { PageHeader, Th, Td } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { FileText, UploadCloud, AlertTriangle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

export default function InvoiceImport() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [prev, setPrev] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [header, setHeader] = useState({});
  const [lines, setLines] = useState([]);

  useEffect(() => { api.get("/organizations").then((r) => setOrgs([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {}); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/import/invoice/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPrev(r.data); setHeader(r.data.header); setLines(r.data.lines); setOrgId(r.data.matched_org?.id || ""); setStep(2);
      toast.success(t("ii.extracted"));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const updLine = (i, k, v) => { const l = [...lines]; l[i] = { ...l[i], [k]: v }; setLines(l); };

  const confirm = async (force = false) => {
    setBusy(true);
    try {
      const mapped = lines.filter((l) => l.matched_variant_id);
      if (!mapped.length) { toast.error(t("ii.needMapping")); setBusy(false); return; }
      const payload = { organization_id: orgId || null, invoice_number: header.invoice_number, invoice_date: header.date || "", currency: header.currency, force,
        pdf_path: prev.pdf_path || "", pdf_filename: prev.pdf_filename || "",
        lines: mapped.map((l) => ({ variant_id: l.matched_variant_id, name: l.description, quantity: Number(l.quantity) || 0, unit_price: Number(l.unit_price) || 0, tax_rate: Number(l.tax_rate) || 19 })) };
      const r = await api.post("/import/invoice/confirm", payload);
      toast.success(t("ii.createdToast", { order: r.data.order_number, invoice: r.data.invoice_number }));
      nav(`/rechnung/${r.data.invoice_id}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        setBusy(false);
        if (window.confirm(e.response.data.detail)) return confirm(true);
        return;
      }
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const mappedCount = lines.filter((l) => l.matched_variant_id).length;

  return (
    <div>
      <PageHeader title={t("ii.title")} subtitle={t("ii.subtitle")} />

      {step === 1 && (
        <div className="rounded-md border border-dashed border-border bg-card p-10 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground mb-4">{t("ii.uploadHint")}</p>
          <label className="inline-flex items-center gap-2 cursor-pointer rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90" data-testid="invoice-upload-label">
            <UploadCloud className="h-4 w-4" /> {busy ? t("ii.extracting") : t("ii.choosePdf")}
            <input type="file" accept=".pdf" className="hidden" data-testid="invoice-file-input" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        </div>
      )}

      {step === 2 && prev && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><Label>{t("ii.invNumberSource")}</Label><Input value={header.invoice_number || ""} onChange={(e) => setHeader({ ...header, invoice_number: e.target.value })} className="mt-1.5" data-testid="inv-number" /></div>
            <div><Label>{t("ii.invDate")}</Label><Input value={header.date || ""} onChange={(e) => setHeader({ ...header, date: e.target.value })} className="mt-1.5" placeholder="TT.MM.JJJJ" data-testid="inv-date" /></div>
            <div><Label>{t("set.currency")}</Label>
              <Select value={header.currency || "EUR"} onValueChange={(v) => setHeader({ ...header, currency: v })}>
                <SelectTrigger className="mt-1.5" data-testid="inv-currency"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="EUR">EUR</SelectItem><SelectItem value="USD">USD</SelectItem><SelectItem value="CAD">CAD</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>{t("ii.b2bCustomer")}{prev.header?.buyer_name ? ` (${t("ii.detected", { name: prev.header.buyer_name })})` : ""}</Label>
              <Select value={orgId || "none"} onValueChange={(v) => setOrgId(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5" data-testid="inv-org"><SelectValue placeholder={t("ii.chooseCustomer")} /></SelectTrigger>
                <SelectContent><SelectItem value="none">{t("ii.noCustomer")}</SelectItem>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("ii.pos")}</Th><Th>{t("ii.descFromInvoice")}</Th><Th>{t("ii.productMapping")}</Th><Th right>{t("common.quantity")}</Th><Th right>{t("ii.unitPrice")}</Th><Th right>{t("set.vatPercent")}</Th><Th></Th></tr></thead>
              <tbody data-testid="invoice-lines-table">
                {lines.map((l, i) => (
                  <tr key={i} className={cn(!l.matched_variant_id && "bg-destructive/5")}>
                    <Td mono>{l.position ?? i + 1}</Td>
                    <Td className="max-w-[280px] truncate" title={l.description}>{l.description}</Td>
                    <Td>
                      <Select value={l.matched_variant_id || "none"} onValueChange={(v) => updLine(i, "matched_variant_id", v === "none" ? "" : v)}>
                        <SelectTrigger className="w-64" data-testid={`inv-line-variant-${i}`}><SelectValue placeholder={t("ii.map")} /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="none">{t("common.dontImport")}</SelectItem>
                          {prev.catalog.map((c) => <SelectItem key={c.variant_id} value={c.variant_id}>{c.sku} · {c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Td>
                    <Td right><Input type="number" className="w-16 h-8 ml-auto" value={l.quantity} onChange={(e) => updLine(i, "quantity", e.target.value)} data-testid={`inv-line-qty-${i}`} /></Td>
                    <Td right><Input type="number" className="w-24 h-8 ml-auto" value={l.unit_price} onChange={(e) => updLine(i, "unit_price", e.target.value)} data-testid={`inv-line-price-${i}`} /></Td>
                    <Td right><Input type="number" className="w-16 h-8 ml-auto" value={l.tax_rate} onChange={(e) => updLine(i, "tax_rate", e.target.value)} /></Td>
                    <Td>{l.matched_variant_id
                      ? <span className="inline-flex items-center gap-1 text-xs text-primary"><CheckCircle2 className="h-3.5 w-3.5" /> {l.match_score >= 1 ? "EAN" : `${Math.round(l.match_score * 100)}%`}</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-destructive"><AlertTriangle className="h-3.5 w-3.5" /> {t("common.open")}</span>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => { setStep(1); setPrev(null); }}>{t("common.back")}</Button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">{t("ii.mappedCount", { mapped: mappedCount, total: lines.length })}</span>
              <Button onClick={() => confirm()} disabled={busy || mappedCount === 0} data-testid="invoice-confirm-btn">{t("ii.createOrder")}</Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t("ii.footerNote")}</p>
        </div>
      )}
    </div>
  );
}
